import { type ErrorCodeMap } from './types';

/**
 * Canonical Chronicle (Heidi Remote) error codes. Use these as the
 * `error_code` field on `hardware_*_failed` analytics events so dashboards
 * can group by structured codes rather than free-form strings.
 *
 * The string-union mirrors the keys of `chronicleErrorCodes` below — adding
 * a new code there will require also adding it to this union (TS will warn).
 *
 * Analytics events accept `string` for `error_code` (not just this union) so
 * that codes from other domains (session engine, network, JS-side errors)
 * can also be tracked. This union is the *preferred* set for Chronicle flows.
 */
export type ChronicleErrorCode =
  | 'CHRONICLE_ALREADY_RUNNING'
  | 'CHRONICLE_LIVE_RECORDING_ACTIVE'
  | 'CHRONICLE_DEVICE_NOT_CONNECTED'
  | 'CHRONICLE_NOT_RUNNING'
  | 'CHRONICLE_NOT_PAUSED'
  | 'CHRONICLE_DOWNLOAD_ABORTED'
  | 'CHRONICLE_CANCELLED'
  | 'CHRONICLE_TRANSFER_ERROR'
  | 'CHRONICLE_DECRYPTION_FAILED'
  | 'CHRONICLE_RECORD_NOT_FOUND'
  | 'CHRONICLE_FILE_NOT_FOUND'
  | 'CHRONICLE_DATABASE_ERROR'
  | 'CHRONICLE_IO_ERROR'
  | 'CHRONICLE_BLE_ERROR'
  | 'CHRONICLE_BLE_WRITE_TIMEOUT'
  | 'CHRONICLE_PERMISSION_DENIED'
  | 'CHRONICLE_BINDING_CODE_MISMATCH'
  | 'CHRONICLE_RECONNECT_NEEDED'
  | 'CHRONICLE_AUTH_ERROR'
  | 'CHRONICLE_NOT_IMPLEMENTED'
  | 'CHRONICLE_INVALID_ARGUMENT'
  | 'CHRONICLE_INTERNAL'
  | 'CHRONICLE_BLUETOOTH_ERROR'
  | 'CHRONICLE_DEVICE_NOT_FOUND'
  | 'CHRONICLE_CONNECTION_ERROR'
  | 'CHRONICLE_PROTOCOL_ERROR'
  | 'CHRONICLE_SYNC_ERROR'
  | 'CHRONICLE_DOWNLOAD_INTERRUPTED'
  | 'CHRONICLE_HTTP_ERROR'
  | 'CHRONICLE_OTA_HANDSHAKE_TIMEOUT'
  | 'CHRONICLE_OTA_FRAME_WRITE_FAILED'
  | 'CHRONICLE_OTA_COMPLETION_TIMEOUT'
  | 'CHRONICLE_OTA_DEVICE_ERROR'
  | 'CHRONICLE_OTA_CANCELLED'
  | 'CHRONICLE_FIRMWARE_DOWNLOAD_FAILED'
  | 'CHRONICLE_FIRMWARE_METADATA_FAILED'
  | 'CHRONICLE_BATTERY_TOO_LOW'
  | 'CHRONICLE_OTA_ALREADY_ACTIVE'
  | 'CHRONICLE_OTA_IN_PROGRESS'
  | 'CHRONICLE_UNSYNCED_RECORDINGS_REMAIN'
  | 'CHRONICLE_OTA_DEVICE_NOT_READY';

export const chronicleErrorCodes: ErrorCodeMap = {
  // src-tauri chronicle module errors (flow through IpcResponse)
  CHRONICLE_ALREADY_RUNNING: 'desktop.errors.chronicle.alreadyRunning',
  CHRONICLE_LIVE_RECORDING_ACTIVE: 'desktop.errors.chronicle.liveRecordingActive',
  CHRONICLE_DEVICE_NOT_CONNECTED: 'desktop.errors.chronicle.deviceNotConnected',
  CHRONICLE_NOT_RUNNING: 'desktop.errors.chronicle.notRunning',
  CHRONICLE_NOT_PAUSED: 'desktop.errors.chronicle.notPaused',
  CHRONICLE_DOWNLOAD_ABORTED: 'desktop.errors.chronicle.downloadAborted',
  CHRONICLE_CANCELLED: 'desktop.errors.chronicle.cancelled',
  CHRONICLE_TRANSFER_ERROR: 'desktop.errors.chronicle.transferError',
  CHRONICLE_DECRYPTION_FAILED: 'desktop.errors.chronicle.decryptionFailed',
  CHRONICLE_RECORD_NOT_FOUND: 'desktop.errors.chronicle.recordNotFound',
  CHRONICLE_FILE_NOT_FOUND: 'desktop.errors.chronicle.fileNotFound',
  CHRONICLE_DATABASE_ERROR: 'desktop.errors.chronicle.databaseError',
  CHRONICLE_IO_ERROR: 'desktop.errors.chronicle.ioError',
  CHRONICLE_BLE_ERROR: 'desktop.errors.chronicle.bleError',
  CHRONICLE_BLE_WRITE_TIMEOUT: 'desktop.errors.chronicle.bleError',
  CHRONICLE_PERMISSION_DENIED: 'desktop.errors.chronicle.permissionDenied',
  CHRONICLE_BINDING_CODE_MISMATCH: 'desktop.errors.chronicle.bindingCodeMismatch',
  CHRONICLE_RECONNECT_NEEDED: 'desktop.errors.chronicle.reconnectNeeded',
  CHRONICLE_AUTH_ERROR: 'desktop.errors.chronicle.authError',

  // tauri-plugin-chronicle errors
  CHRONICLE_NOT_IMPLEMENTED: 'desktop.errors.chronicle.notImplemented',
  CHRONICLE_INVALID_ARGUMENT: 'desktop.errors.chronicle.invalidArgument',
  CHRONICLE_INTERNAL: 'desktop.errors.chronicle.internal',
  CHRONICLE_BLUETOOTH_ERROR: 'desktop.errors.chronicle.bluetoothError',
  CHRONICLE_DEVICE_NOT_FOUND: 'desktop.errors.chronicle.deviceNotFound',
  CHRONICLE_CONNECTION_ERROR: 'desktop.errors.chronicle.connectionError',
  CHRONICLE_PROTOCOL_ERROR: 'desktop.errors.chronicle.protocolError',
  CHRONICLE_SYNC_ERROR: 'desktop.errors.chronicle.syncFailed',
  CHRONICLE_DOWNLOAD_INTERRUPTED: 'desktop.errors.chronicle.downloadInterrupted',
  CHRONICLE_HTTP_ERROR: 'desktop.errors.chronicle.httpError',

  // OTA firmware update errors
  CHRONICLE_OTA_HANDSHAKE_TIMEOUT: 'desktop.errors.chronicle.otaHandshakeTimeout',
  CHRONICLE_OTA_FRAME_WRITE_FAILED: 'desktop.errors.chronicle.otaFrameWriteFailed',
  CHRONICLE_OTA_COMPLETION_TIMEOUT: 'desktop.errors.chronicle.otaCompletionTimeout',
  CHRONICLE_OTA_DEVICE_ERROR: 'desktop.errors.chronicle.otaDeviceError',
  CHRONICLE_OTA_CANCELLED: 'desktop.errors.chronicle.otaCancelled',
  CHRONICLE_FIRMWARE_DOWNLOAD_FAILED: 'desktop.errors.chronicle.firmwareDownloadFailed',
  CHRONICLE_FIRMWARE_METADATA_FAILED: 'desktop.errors.chronicle.firmwareMetadataFailed',
  CHRONICLE_BATTERY_TOO_LOW: 'desktop.errors.chronicle.batteryTooLow',
  CHRONICLE_OTA_ALREADY_ACTIVE: 'desktop.errors.chronicle.otaAlreadyActive',
  CHRONICLE_OTA_IN_PROGRESS: 'desktop.errors.chronicle.otaInProgress',
  CHRONICLE_UNSYNCED_RECORDINGS_REMAIN:
    'desktop.errors.chronicle.unsyncedRecordingsRemain',
  CHRONICLE_OTA_DEVICE_NOT_READY: 'desktop.errors.chronicle.otaDeviceNotReady',
};
